import moment from 'moment-timezone';

import { isInternal } from '../components/atoms/adminOnly';
import { EventStatus, FleetUUIDs } from '../enums';
import { Event, Fleet, MissedEvent } from '../interfaces';
import { getEnforcementPeriods } from './eventDetailsHelpers';
import { isWhitelisted } from './isWhitelisted';
import { getSentToProcessorStatus } from './sentToProcessorStatus';

const FLEETS_WITH_EXTENDED_CSV = [FleetUUIDs.WMATA, FleetUUIDs.CulverCity];

const findParentEvent = (currentEventId: number, events: Event[]): Event | undefined => {
  const parentEvent = events.find((e) => e.sub_events && e.sub_events?.[1]?.id === currentEventId);

  return parentEvent && parentEvent.sub_events?.[0];
};

const findMatch = (currentEventId: number, events: Event[]): Event | undefined => {
  const parentEvent = events.find((e) => e.sub_events && e.sub_events?.[1]?.id === currentEventId);

  return parentEvent;
};

// Default CSV report, accepts all event types
const DefaultCsvReport = async (
  events: Event[],
  missedEvents: MissedEvent[],
  timezone: string,
  fleet: Fleet,
  withNotes = false,
  withExternalReview = false,
): Promise<string[]> => {
  const hasAccess: boolean = await isInternal();
  const lines: string[][] = [];
  let headers: string[] = [];
  headers.push('ID', 'Plate');
  if (useExtendedCSV(fleet)) {
    headers.push('License Plate State', 'lat', 'long');
  }
  headers.push(
    'Date',
    'Time',
    'Vehicle',
    'Route',
    'Direction',
    'Location',
    'Bus Stop ID',
    'Permitted',
    'Event type',
    'In enforcement zone',
    'In enforcement period',
    'Enforcement periods',
    'Type',
    'Parent ID',
    'Processor Delivery Status',
    'Processor Delivery Date',
    'Connection Type',
  );

  const internalOnlyColumns = ['Context image', 'Plate image', 'ALPR image'];

  if (hasAccess) {
    headers = [...headers, ...internalOnlyColumns];
  }

  if (withNotes) {
    headers.push('Human review');
    headers.push('Notes');
  }

  if (withExternalReview) {
    headers.push('Review');
  }

  events.forEach((e) => {
    const parentEvent = findParentEvent(e.id, events);
    const matchedContainer = findMatch(e.id, events);
    const whitelisted = isWhitelisted(e);

    const line: string[] = [];
    line.push(String(e.id));
    line.push(e.license_plate);
    if (useExtendedCSV(fleet)) {
      line.push(getStateFromEvent(e));
      line.push(String(e.lat));
      line.push(String(e.lon));
    }
    line.push(moment.tz(e.captured_at, timezone).format('YYYY-MM-DD'));
    line.push(moment.tz(e.captured_at, timezone).format('hh:mm:ss A'));
    line.push(e.vehicle_name ?? '');
    line.push(e.route_name ?? '');
    line.push(e.metadata?.route_metadata?.route_direction ?? '');
    line.push(e.metadata?.route_metadata?.block_name ?? ''.replace(/,/g, ''));
    line.push(e.metadata?.route_metadata?.block_id ?? '');
    line.push(whitelisted ? 'PERMITTED' : '-');
    line.push(e.event_type_identifier);
    line.push(e.within_enforce_zone === false ? 'NOT IN ENFORCEMENT ZONE' : 'IN ENFORCEMENT ZONE');
    line.push(e.within_enforce_time ? 'IN ENFORCEMENT PERIOD' : 'NOT IN ENFORCEMENT PERIOD');
    line.push(getEnforcementPeriods(e).join(',').replace(',', ' - '));
    line.push(e.human_review === false ? 'FP' : 'TP');
    line.push(parentEvent && !whitelisted ? String(parentEvent.id) : '-');
    line.push(getSentToProcessorStatus(matchedContainer || e));
    const evidenceDelivertyDate = matchedContainer?.evidence_delivered_at || e.evidence_delivered_at;
    if (evidenceDelivertyDate) {
      line.push(moment.tz(evidenceDelivertyDate, timezone).format('YYYY-MM-DD'));
    } else {
      line.push('-');
    }
    line.push(e.connection_type ?? '');

    if (hasAccess) {
      line.push(getImageName(e));
      line.push(getLpImageName(e));
      line.push(getALPRImage(e));
    }

    if (withNotes) {
      line.push(e.human_review === false ? 'FALSE' : 'TRUE');
      line.push('-');
    }

    if (withExternalReview) {
      if (e.status === EventStatus.approved) {
        line.push('APPROVED');
      } else if (e.status === EventStatus.rejected) {
        line.push('REJECTED');
      } else if (e.status === EventStatus.approved_warning) {
        line.push('WARNING');
      } else {
        line.push('UNREVIEWED');
      }
    }

    lines.push(line);
  });

  missedEvents?.forEach?.((e) => {
    const line: string[] = [];
    line.push('-');
    line.push(e.license_plate);
    if (useExtendedCSV(fleet)) {
      line.push('-');
      line.push(String(e.lat));
      line.push(String(e.lon));
    }
    line.push(moment.tz(e.absoluteTime, timezone).format('YYYY-MM-DD'));
    line.push(moment.tz(e.absoluteTime, timezone).format('hh:mm:ss A'));
    line.push(events[0]?.vehicle_name ?? '');
    line.push(events[0]?.route_name ?? '');
    line.push('-');
    line.push('-');
    line.push('-');
    line.push(isWhitelisted(e) ? 'PERMITTED' : '-');
    line.push('IN BUS LANE');
    line.push('IN ENFORCEMENT ZONE');
    line.push(e.within_enforce_time ? 'IN ENFORCEMENT PERIOD' : 'NOT IN ENFORCEMENT PERIOD');
    line.push('-');
    line.push('-');
    line.push('FN');
    line.push('-');
    line.push('-');

    if (hasAccess) {
      line.push('-');
      line.push('-');
      line.push('-');
    }

    if (withNotes) {
      line.push('-');
      line.push(e.notes ?? '');
    }

    lines.push(line);
  });

  lines.unshift(headers);
  const csv = lines.map((l) => l.join(','));
  return csv;
};

// Map should only include fleets that need a specific report. all other fleets will automatically use the default report
const fleetToGeneratorMap = new Map<string, Function>([['example_fleet_uuid', DefaultCsvReport]]);

export const generateCSV = async (
  events: Event[],
  missedEvents: MissedEvent[],
  timezone: string,
  fleet: Fleet,
  withNotes = false,
  withExternalReview = false,
): Promise<string[]> => {
  if (!fleetToGeneratorMap.has(fleet.uuid)) {
    return DefaultCsvReport(events, missedEvents, timezone, fleet, withNotes, withExternalReview);
  }

  const func = fleetToGeneratorMap.get(fleet.uuid);
  return func?.call(func, events, missedEvents, timezone, fleet, withNotes, withExternalReview);
};

const getImageName = (e: Event): string => {
  const sub_events: Event[] = e.sub_events ?? [];
  const media = e.event_videos || (sub_events.length > 0 ? sub_events[0].event_videos : []);
  const presignedUrl = media.length > 0 ? media[0].license_plate_url : '';
  const iof = presignedUrl.indexOf('?');
  const lastSlash = presignedUrl.lastIndexOf('/');
  const url = presignedUrl.substr(lastSlash, iof - lastSlash);
  return `images${url}`;
};

const getLpImageName = (e: Event): string => {
  const sub_events: Event[] = e.sub_events ?? [];
  const media = e.event_videos || (sub_events.length > 0 ? sub_events[0].event_videos : []);
  const presignedUrl = media.length > 0 ? media[0].license_plate_url : '';
  const iof = presignedUrl.indexOf('?');
  const lastSlash = presignedUrl.lastIndexOf('/');
  const url = presignedUrl.substr(lastSlash, iof - lastSlash);
  return `images${url}`;
};

const getALPRImage = (e: Event): string => {
  const sub_events: Event[] = e.sub_events ?? [];
  const media = e.event_videos || (sub_events.length > 0 ? sub_events[0].event_videos : []);
  let presignedUrl = media.length > 0 ? media[0].license_plate_url : '';
  presignedUrl = presignedUrl.replace('-lp.jpg', '-alpr.jpg');
  const iof = presignedUrl.indexOf('?');
  const lastSlash = presignedUrl.lastIndexOf('/');
  const url = presignedUrl.substr(lastSlash, iof - lastSlash);
  return `images${url}`;
};

const useExtendedCSV = (fleet: Fleet) => {
  return FLEETS_WITH_EXTENDED_CSV.includes(fleet.uuid as FleetUUIDs);
};

const getStateFromEvent = (e: Event): string => {
  let state = '';
  state = e?.license_plate_region ?? '';

  const iof = state.indexOf('-');
  if (iof > -1) {
    return state.substring(iof + 1).toUpperCase();
  }
  return state?.toUpperCase();
};
